import { FC, useCallback, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { api } from 'services/api';
import { TPgAuditEntry } from 'services/api/paymentGateway/types';
import { DATE_FILTER } from 'ui/EasTable/constants';
import { ESortDirection, ETableStructureType, TTableStructure } from 'ui/EasTable/types';
import { PgFields, PgJsonView } from '../common/PgFields';
import { PgList } from '../common/PgList';
import { PgPage } from '../common/PgPage';
import {
	DETAILS_BUTTON,
	humanize,
	pgActionsColumn,
	pgColumn,
	statusColumn,
} from '../common/tableHelpers';
import { formatDateTime } from '../utils';

type TRow = TPgAuditEntry;
const col = (
	name: string,
	selector: keyof TRow & string,
	type: ETableStructureType,
	extra: Partial<TTableStructure<keyof TRow, TRow>> = {},
) => pgColumn<TRow>(name, selector, type, extra);

const STRUCTURE: TTableStructure<keyof TRow, TRow>[] = [
	col('Time', 'occurredAt', ETableStructureType.DATE_TIME, {
		sortable: true,
		filterable: true,
		filter: DATE_FILTER,
	}),
	col('Actor type', 'actorType', ETableStructureType.STATUS, {
		sortable: true,
		filterable: true,
		...statusColumn(['admin', 'customer', 's2s', 'system']),
	}),
	col('Actor', 'actorId', ETableStructureType.STRING, {
		sortable: true,
		filterable: true,
		searchable: true,
		value: (item) => item.actorName ?? item.actorId,
		tooltip: (item) => item.actorId,
	}),
	col('Action', 'action', ETableStructureType.STRING, { sortable: true, filterable: true }),
	col('Entity', 'entityType', ETableStructureType.STRING, { sortable: true, filterable: true }),
	col('Entity ID', 'entityId', ETableStructureType.STRING, { sortable: true, filterable: true }),
	col('IP', 'ip', ETableStructureType.STRING),
	pgActionsColumn<TRow>([DETAILS_BUTTON]),
];

/** Audit log of the gateway (03 §4.12): who changed what, with the before / after data. */
export const Audit: FC = () => {
	const [entry, setEntry] = useState<TRow | null>(null);

	const handleAction = useCallback((row: TRow, action: string) => {
		if (action === DETAILS_BUTTON.name) setEntry(row);
	}, []);

	return (
		<PgPage title="Audit">
			<PgList<TRow>
				title="Audit log"
				structure={STRUCTURE}
				fetcher={api.paymentGateway.getAudit}
				onAction={handleAction}
				defaultOrder={{ fieldName: 'occurredAt', direction: ESortDirection.DESC }}
				errorText="Failed to load the audit log"
			/>
			<Dialog open={!!entry} onClose={() => setEntry(null)} maxWidth="md" fullWidth>
				<DialogTitle color="primary">{entry?.action ?? 'Audit entry'}</DialogTitle>
				<DialogContent dividers>
					{entry && (
						<>
							<PgFields
								items={[
									['Time', formatDateTime(entry.occurredAt)],
									['Actor', `${entry.actorName ?? entry.actorId} (${humanize(entry.actorType)})`],
									['Actor ID', entry.actorId],
									['Entity', `${entry.entityType}${entry.entityId ? ` #${entry.entityId}` : ''}`],
									['IP', entry.ip],
									['Correlation ID', entry.correlationId],
								]}
							/>
							<PgJsonView value={entry.data} maxHeight={420} />
						</>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setEntry(null)}>Close</Button>
				</DialogActions>
			</Dialog>
		</PgPage>
	);
};
